import {coloringIconsCss} from "@frontend/design"
import {activeTheme} from "@frontend/themes/activeTheme"
import {RecipeVariant, cva} from "@styled-system/css"

const {buttonEmphasis, buttonFocusRingStyle} = activeTheme.capabilities
const isUpperCaseEmphasis = buttonEmphasis === "upperCase"
const hasFocusRing = buttonFocusRingStyle === "ring"

/**
 * Button color variants.
 *
 * `buttonEmphasis`/`buttonFocusRingStyle` are theme capabilities (see
 * `themes/types.ts`), not a brand check — Bodewell currently sets
 * `upperCase`/`none`, every other brand uses the shared defaults.
 *
 * Bodewell brand mapping (RvR BODEWELL THEME.css):
 * - primary: slate #245B83 fill, cream text, semibold → hover dusty #728A9C
 * - secondary: yellow #FFC01F fill, navy text, bold → hover cream text (bg stays yellow)
 * - secondarySolid: yellow outline → hover yellow fill + navy text
 * - tertiaryBrand: slate outline → hover slate fill + cream text (bold)
 */
export const buttonColors = cva({
  // Uppercase brands (Bodewell): primary is semibold; secondary/tertiary override to bold
  base: isUpperCaseEmphasis
    ? {
        textTransform: "uppercase",
        letterSpacing: "0.4px",
        fontWeight: 600,
      }
    : {},
  variants: {
    variant: {
      unstyled: {},
      primary: {
        backgroundColor: "background.brand.accent",
        color: "text.primary.onSolid",
        border: "1px solid token(colors.background.brand.accent)",
        boxShadow: hasFocusRing ? "0px -2px 0px 0px #1018280D inset" : "none",
        _hover: {
          backgroundColor: "background.brand.accentSolidHover",
          borderColor: "background.brand.accentSolidHover",
          color: "text.primary.onSolid",
        },
        _focus: {
          backgroundColor: "background.brand.accentSolid",
          boxShadow: hasFocusRing
            ? "0px 0px 0px 6px token(colors.background.subtle.brand), inset 0px -2px 0px rgba(16, 24, 40, 0.05)"
            : "none",
        },
        ...coloringIconsCss("currentColor"),
      },
      // Filled yellow (CSS .button-style-secondary): text navy → cream on hover
      // Icons use currentColor so they track text color on hover.
      secondary: {
        backgroundColor: "brandAccent.600",
        color: "brand.800",
        border: "1px solid token(colors.brandAccent.600)",
        ...(isUpperCaseEmphasis ? {fontWeight: 700} : {}),
        ...coloringIconsCss("currentColor"),
        _hover: {
          backgroundColor: "brandAccent.600",
          borderColor: "brandAccent.600",
          color: "text.primary.onSolid",
        },
        _focus: {
          backgroundColor: "brandAccent.600",
          color: "text.primary.onSolid",
          boxShadow: hasFocusRing
            ? "0px 0px 0px 6px token(colors.background.subtle.brand)"
            : "none",
        },
      },
      // Outlined accent (Bodewell "Secondary Solid")
      secondarySolid: {
        backgroundColor: "transparent",
        color: "text.brand.accent",
        border: "1px solid token(colors.border.brand.accent)",
        ...(isUpperCaseEmphasis ? {fontWeight: 700} : {}),
        ...coloringIconsCss("currentColor"),
        _hover: {
          backgroundColor: "brandAccent.600",
          color: "brand.800",
        },
        _focus: {
          backgroundColor: "brandAccent.600",
          color: "brand.800",
          boxShadow: hasFocusRing
            ? "0px 0px 0px 6px token(colors.background.subtle.brand)"
            : "none",
        },
      },
      secondaryOnBrand: {
        backgroundColor: "transparent",
        color: "text.secondary.onSolid",
        border: "1px solid token(colors.foreground.secondaryOnSolid)",
        ...coloringIconsCss("currentColor"),
        _hover: {
          backgroundColor: "background.brand.solidSubtle",
          color: "text.primary.onSolid",
        },
        _focus: {
          backgroundColor: "background.brand.solidSubtle",
          color: "text.primary.onSolid",
          boxShadow:
            "0px 0px 0px 6px token(colors.background.brand.solidSubtle), inset 0px -2px 0px rgba(16, 24, 40, 0.05)",
        },
      },
      secondaryGray: {
        backgroundColor: "transparent",
        color: "gray.600",
        border: "1px solid token(colors.border.secondary.solid)",
        ...coloringIconsCss("currentColor"),
        _hover: {
          backgroundColor: "background.secondary",
        },
        _focus: {
          backgroundColor: "background.primary",
          boxShadow: "0px 0px 0px 6px token(colors.background.tertiary)",
        },
      },
      // Bodewell "Tertiary": slate outline → fill on hover
      tertiaryBrand: {
        backgroundColor: "transparent",
        color: "text.brand.primary",
        border: "1px solid token(colors.border.brand.secondarySolid)",
        ...(isUpperCaseEmphasis ? {fontWeight: 700} : {}),
        ...coloringIconsCss("currentColor"),
        _hover: {
          backgroundColor: "background.brand.accent",
          color: "text.primary.onSolid",
          borderColor: "background.brand.accent",
        },
        _focus: {
          backgroundColor: "background.brand.accent",
          color: "text.primary.onSolid",
          boxShadow: hasFocusRing
            ? "0px 0px 0px 6px token(colors.background.brand.primary)"
            : "none",
        },
      },
      tertiaryBrandAccent: {
        backgroundColor: "transparent",
        color: "text.brand.accent",
        border: "1px solid token(colors.border.brand.accent)",
        ...(isUpperCaseEmphasis ? {fontWeight: 700} : {}),
        ...coloringIconsCss("currentColor"),
        _hover: {
          backgroundColor: "brandAccent.600",
          color: "brand.800",
        },
        _focus: {
          backgroundColor: "brandAccent.600",
          color: "brand.800",
          boxShadow: hasFocusRing
            ? "0px 0px 0px 6px token(colors.background.subtle.brand)"
            : "none",
        },
      },
    },
  },
  defaultVariants: {
    variant: "primary",
  },
})

export type ButtonVariantsProps = RecipeVariant<typeof buttonColors>

export const disabledStyles = cva({
  base: {
    pointerEvents: "none",
  },
  variants: {
    variant: {
      unstyled: {},
      primary: {
        backgroundColor: "background.disabled",
        color: "text.disabled",
        border: "1px solid token(colors.border.disabled)",
        boxShadow: "none",
        ...coloringIconsCss("foreground.disabled"),
      },
      secondary: {
        backgroundColor: "background.disabled",
        color: "text.disabled",
        border: "1px solid token(colors.border.disabled)",
        boxShadow: "none",
        ...coloringIconsCss("foreground.disabled"),
      },
      secondarySolid: {
        backgroundColor: "background.primary",
        color: "text.disabled",
        border: "1px solid token(colors.border.disabled)",
        boxShadow: "none",
        ...coloringIconsCss("foreground.disabled"),
      },
      secondaryGray: {
        backgroundColor: "background.primary",
        color: "text.disabled",
        border: "1px solid token(colors.border.disabled)",
        boxShadow: "none",
        ...coloringIconsCss("foreground.disabled"),
      },
      secondaryOnBrand: {
        backgroundColor: "background.primary",
        color: "text.disabled",
        border: "1px solid token(colors.border.disabled)",
        boxShadow: "none",
        ...coloringIconsCss("foreground.disabled"),
      },
      tertiaryBrand: {
        backgroundColor: "transparent",
        color: "text.disabled",
        border: "1px solid token(colors.border.disabled)",
        boxShadow: "none",
        ...coloringIconsCss("foreground.disabled"),
      },
      tertiaryBrandAccent: {
        backgroundColor: "transparent",
        color: "text.disabled",
        border: "1px solid token(colors.border.disabled)",
        boxShadow: "none",
        ...coloringIconsCss("foreground.disabled"),
      },
    },
  },
})
